<script>
  import { createEventDispatcher } from 'svelte';
  export let errors = [];
  const dispatch = createEventDispatcher();

  function formatTime(ts) {
    const d = new Date(ts);
    return d.toLocaleTimeString('en-IN', { hour: '2-digit', minute: '2-digit', timeZone: 'Asia/Kolkata' }) + ' IST';
  }

  function formatDate(ts) {
    const d = new Date(ts);
    return d.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', timeZone: 'Asia/Kolkata' });
  }

  function getSeverityConfig(severity) {
    switch (severity) {
      case 'critical': return { color: 'var(--neon-red)', bg: 'rgba(239, 68, 68, 0.08)' };
      case 'warning': return { color: 'var(--neon-orange)', bg: 'rgba(245, 158, 11, 0.08)' };
      default: return { color: 'var(--neon-blue)', bg: 'rgba(59, 130, 246, 0.08)' };
    }
  }
</script>

<div class="error-log-card card">
  <div class="log-header">
    <div class="card-title">
      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
      </svg>
      Recent Errors
      {#if errors.length > 0}
        <span class="error-count">{errors.length}</span>
      {/if}
    </div>
    {#if errors.length > 0}
      <button class="btn-danger" on:click={() => dispatch('clear')} id="clear-errors-btn">
        Clear All
      </button>
    {/if}
  </div>

  {#if errors.length === 0}
    <div class="no-errors">
      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
        <path d="M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z"/><polyline points="9 12 11 14 15 10"/>
      </svg>
      <span>No recent errors — all systems clean</span>
    </div>
  {:else}
    <div class="error-list" id="error-list">
      {#each errors as error}
        {@const cfg = getSeverityConfig(error.severity)}
        <div class="error-entry" style="border-left-color: {cfg.color};">
          <div class="error-main">
            <div class="error-dot" style="background: {cfg.color}; box-shadow: 0 0 6px {cfg.color};"></div>
            <div class="error-content">
              <span class="error-app">{error.app}</span>
              <span class="error-msg">{error.message}</span>
            </div>
          </div>
          <div class="error-time">
            <span class="error-date">{formatDate(error.timestamp)}</span>
            <span class="error-clock">{formatTime(error.timestamp)}</span>
          </div>
        </div>
      {/each}
    </div>
  {/if}
</div>

<style>
  .error-log-card {
    animation: fadeInUp 0.4s ease 0.1s both;
  }

  .log-header {
    display: flex;
    align-items: center;
    justify-content: space-between;
    margin-bottom: 16px;
  }

  .log-header .card-title {
    display: flex;
    align-items: center;
    gap: 8px;
    margin-bottom: 0;
  }

  .log-header .card-title svg {
    color: var(--neon-red);
  }

  .error-count {
    background: rgba(239, 68, 68, 0.2);
    color: var(--neon-red);
    font-size: 11px;
    font-weight: 700;
    padding: 2px 8px;
    border-radius: 10px;
  }

  .no-errors {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 24px;
    color: var(--text-secondary);
    font-size: 14px;
  }

  .no-errors svg {
    color: var(--neon-green);
    flex-shrink: 0;
  }

  .error-list {
    display: flex;
    flex-direction: column;
    gap: 6px;
    max-height: 400px;
    overflow-y: auto;
  }

  .error-entry {
    display: flex;
    align-items: center;
    justify-content: space-between;
    padding: 12px 14px;
    background: rgba(255, 255, 255, 0.02);
    border-radius: var(--radius-sm);
    border-left: 3px solid transparent;
    transition: background 0.2s ease;
  }

  .error-entry:hover {
    background: rgba(255, 255, 255, 0.04);
  }

  .error-main {
    display: flex;
    align-items: center;
    gap: 12px;
    flex: 1;
    min-width: 0;
  }

  .error-dot {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    flex-shrink: 0;
  }

  .error-content {
    display: flex;
    flex-direction: column;
    gap: 2px;
    min-width: 0;
  }

  .error-app {
    font-size: 13px;
    font-weight: 600;
    color: var(--text-primary);
  }

  .error-msg {
    font-size: 12px;
    color: var(--text-secondary);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .error-time {
    display: flex;
    flex-direction: column;
    align-items: flex-end;
    gap: 1px;
    flex-shrink: 0;
    margin-left: 16px;
  }

  .error-date {
    font-size: 11px;
    color: var(--text-tertiary);
  }

  .error-clock {
    font-size: 12px;
    color: var(--text-secondary);
    font-weight: 500;
  }

  @media (max-width: 768px) {
    .error-entry { flex-direction: column; align-items: flex-start; gap: 6px; }
    .error-time { flex-direction: row; gap: 8px; margin-left: 20px; }
  }
</style>
